'use client'

import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useShow } from '@/contexts/ShowContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

const WS_URL = process.env.NEXT_PUBLIC_WS_URL;
const WS_RECONNECT_BASE_DELAY = 1000;
const WS_RECONNECT_MAX_DELAY = 30000;

export function WebSocketProvider({ children }) {
  const { user } = useUser();
  const { show } = useShow();
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const intentionalDisconnectRef = useRef(false);
  const [status, setStatus] = useState('disconnected');
  const feedsRef = useRef([]);
  const extraFeedsRef = useRef(new Map());

  const broadcast = useCallback((message) => {
    listenersRef.current.forEach((callback) => {
      try {
        callback(message);
      } catch (err) {
        console.error('[WS] Error in listener:', err);
      }
    });
  }, []);

  const sendToServer = useCallback((action, payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action, ...payload }));
    }
  }, []);

  const connect = useCallback(() => {
    if (!WS_URL) return;
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return;

    const token = document.cookie.split('; ').find(c => c.startsWith('auth='))?.split('=')[1];
    if (!token) return;

    intentionalDisconnectRef.current = false;
    setStatus('connecting');
    const ws = new WebSocket(`${WS_URL}?token=${token}`);

    ws.onopen = () => {
      setStatus('connected');
      reconnectAttemptRef.current = 0;

      const allFeeds = [
        ...feedsRef.current,
        ...extraFeedsRef.current.keys(),
      ];
      if (allFeeds.length > 0) {
        ws.send(JSON.stringify({ action: 'subscribe', feeds: allFeeds }));
      }
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        broadcast(message);
      } catch (err) {
        console.error('[WS] Failed to parse message:', err);
      }
    };

    ws.onclose = () => {
      setStatus('disconnected');
      wsRef.current = null;

      if (!intentionalDisconnectRef.current) {
        scheduleReconnect();
      }
    };

    ws.onerror = () => {
      setStatus('disconnected');
    };

    wsRef.current = ws;
  }, [broadcast]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return;

    const attempt = reconnectAttemptRef.current;
    const delay = Math.min(WS_RECONNECT_BASE_DELAY * Math.pow(2, attempt), WS_RECONNECT_MAX_DELAY);
    reconnectAttemptRef.current = attempt + 1;

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connect();
    }, delay);
  }, [connect]);

  const disconnect = useCallback(() => {
    intentionalDisconnectRef.current = true;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }
    return () => disconnect();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' &&
          (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
        reconnectAttemptRef.current = 0;
        connect();
      }
    };

    const handleOnline = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reconnectAttemptRef.current = 0;
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [user, connect]);

  useEffect(() => {
    if (status !== 'connected' || !show?._id || !user?.id) return;

    const feeds = [
      `show/${show._id}`,
      `show/${show._id}/user/${user.id}`,
    ];
    feedsRef.current = feeds;

    sendToServer('subscribe', { feeds });

    const toastHandler = (message) => {
      if (message?.event !== 'user_notification') return;
      const data = message.data;
      if (!data?.message) return;

      const isExternal = data.link?.startsWith('http');
      toast(data.message, {
        description: data.description,
        duration: 5000,
        icon: data.icon ? <img src={data.icon} alt="" className="w-5 h-5 rounded" /> : undefined,
        action: data.link ? {
          label: 'View',
          onClick: () => {
            if (isExternal) {
              window.open(data.link, '_blank', 'noopener,noreferrer');
            } else {
              window.location.href = data.link;
            }
          },
        } : undefined,
        cancel: {
          label: 'Dismiss',
          onClick: () => {},
        },
      });
    };

    listenersRef.current.add(toastHandler);

    return () => {
      listenersRef.current.delete(toastHandler);
      sendToServer('unsubscribe', { feeds: feedsRef.current });
      feedsRef.current = [];
    };
  }, [status, show?._id, user?.id, sendToServer]);

  const addListener = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  const subscribe = useCallback((feeds) => {
    const feedList = Array.isArray(feeds) ? feeds : [feeds];
    const map = extraFeedsRef.current;
    const newFeeds = [];

    for (const feed of feedList) {
      const count = map.get(feed) || 0;
      map.set(feed, count + 1);
      if (count === 0) newFeeds.push(feed);
    }

    if (newFeeds.length > 0) {
      sendToServer('subscribe', { feeds: newFeeds });
    }

    return () => {
      const removedFeeds = [];
      for (const feed of feedList) {
        const count = map.get(feed) || 0;
        if (count <= 1) {
          map.delete(feed);
          removedFeeds.push(feed);
        } else {
          map.set(feed, count - 1);
        }
      }
      if (removedFeeds.length > 0) {
        sendToServer('unsubscribe', { feeds: removedFeeds });
      }
    };
  }, [sendToServer]);

  const value = {
    status,
    addListener,
    subscribe,
    sendToServer,
    connect,
    disconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}
